import React, { Fragment, useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Leaf, X, ChevronDown, ChevronUp, ZoomIn, Ungroup, RotateCcw, MessageSquare, MapPin } from 'lucide-react';
import { floatingSurfaceClass } from '../floatingSurfaceStyles';
import { useDismissiblePopover } from '../home/WeatherMapControls';
import { toInitCap } from './weatherConstants';
import { growingStatusColor } from './growingStatuses';
import {
  summarizeCluster,
  regionAreaHa,
  regionProductionT,
  formatCompact,
  formatFull,
  varietiesOf,
  windowsOf,
} from './growingClusterSummary';

// 97 rather than the rail's 94: this is a panel of small figures, and tiles
// showing through behind it cost legibility that icon-led chrome can afford.
const SURFACE = floatingSurfaceClass(97);

const LABEL = "font-['Inter'] text-[11px] leading-[15px] text-[#828382]";
const VALUE = "font-['Inter'] text-[17px] font-bold leading-[22px] text-[#282A29] dark:text-[#E6E8E6]";
const DIVIDER = 'border-[#EEF0EC] dark:border-[#2A302C]';

/** Rows shown before the "show more" link, per the design. */
const COLLAPSED_ROWS = 3;

/** One label/value line inside an opened location row. */
const DetailLine = ({ label, value }) => (
  <div className="flex gap-[6px] py-[2px]">
    <span className="shrink-0 font-['Inter'] text-[11px] leading-[15px] text-[#828382]">{label}</span>
    <span className="min-w-0 font-['Inter'] text-[11px] leading-[15px] text-[#282A29] dark:text-[#E6E8E6]">{value}</span>
  </div>
);

/** One figure in the totals strip: small grey label over a bold value + unit. */
const Total = ({ label, value, unit }) => (
  <div className="min-w-0 flex-1 px-[12px] py-[9px]">
    <p className={LABEL}>{label}</p>
    <p className={`${VALUE} truncate`}>
      {value}
      {unit ? <span className="ml-[3px] text-[12px] font-medium text-[#828382]">{unit}</span> : null}
    </p>
  </div>
);

/**
 * What a cluster pin opens: how many regions it stands for, what they total,
 * and a breakdown by production status.
 *
 * The pin itself can only carry a count and a ring, so this is where a mixed
 * group becomes legible — it is the reason clustering hides nothing.
 *
 * @param {object}   cluster          A `clusterRegions` entry.
 * @param {Function} onClose          Dismisses the panel and deselects the cluster.
 * @param {Function} onZoomToRegions  Closes the panel and fits the map to the cluster.
 * @param {Function} onExpandRegions   Draws the group's locations individually,
 *                                     at the current zoom, panel still open.
 * @param {Function} onCollapseRegions Puts them back behind the one pin.
 * @param {boolean}  expanded          Whether this group is drawn expanded.
 * @param {Function} onAskBiq         Opens BiQ with this cluster as context.
 */
const GrowingClusterOverview = ({
  cluster,
  onClose,
  onZoomToRegions,
  onExpandRegions,
  onCollapseRegions,
  expanded: isExpanded = false,
  onAskBiq,
  className = '',
}) => {
  const { t } = useTranslation();
  const summary = useMemo(() => summarizeCluster(cluster), [cluster]);
  const firstStatus = summary.groups[0]?.status ?? null;

  // Only one group open at a time, the first by default. Keyed off the
  // cluster so opening a different pin starts from its own first group
  // rather than inheriting whatever was open on the last one.
  const [expanded, setExpanded] = useState(firstStatus);
  const [showAll, setShowAll] = useState({});
  // Which location row has its detail open. One at a time: the panel is short
  // and several open rows push the rest of the list out of sight.
  const [openRow, setOpenRow] = useState(null);
  useEffect(() => {
    setExpanded(firstStatus);
    setShowAll({});
    setOpenRow(null);
  }, [cluster?.key, firstStatus]);

  // Clicking the map, another pin or anything else outside drops the
  // selection, as does Escape. The hook fires on `pointerdown`, which lands
  // before Leaflet's marker click — so clicking a second cluster closes this
  // one and then selects that one, rather than the close eating the click.
  const panelRef = useRef(null);
  const dismiss = useCallback((next) => { if (!next) onClose?.(); }, [onClose]);
  useDismissiblePopover(panelRef, true, dismiss);

  if (!cluster || summary.total === 0) return null;

  // A group is a real administrative region now, so it leads with its own
  // name and nothing else: the commodity is already chosen in the filter bar
  // above, and the count lives in the totals strip as "Locations". Groups
  // whose rows carry no region name keep the old count-led heading — there is
  // nothing to name them with.
  const title = summary.regionName
    ? toInitCap(summary.regionName)
    : t('map.growing.clusterTitle', '{{count}} growing regions', { count: summary.total });

  return (
    <div
      ref={panelRef}
      className={`flex min-h-0 w-full flex-col overflow-hidden rounded-[18px] border border-solid shadow-[0px_10px_28px_0px_rgba(17,24,39,0.14)] lg:w-[300px] ${SURFACE} ${className}`}
      role="dialog"
      aria-label={t('map.growing.clusterAria', 'Growing regions in this group')}
    >
      {/* Header */}
      {/* items-center, not items-start. The start alignment dated from when
          this header carried a title AND a subtitle, where the two-line block
          sat naturally beside a 36px tile. With the subtitle gone the title is
          one 19px line, so starting it at the top pinned it to the tile's top
          edge and left the rest of the tile's height as dead space under the
          name — which is what reads as misalignment.

          The tile also comes down to 28px, the same size as the buttons
          opposite it, so the row is one consistent height instead of a tall
          left end and a short right one. Header height: 28 + 10 + 10 = 48. */}
      <div className={`flex shrink-0 items-center gap-[8px] border-b border-solid px-[12px] py-[10px] ${DIVIDER}`}>
        <span className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-[8px] bg-[#F0FDF4] text-[#16A34A] dark:bg-[#1C3023] dark:text-[#7EE6A4]">
          <Leaf size={15} />
        </span>
        <div className="min-w-0 flex-1">
          {/* Wraps rather than truncates: the region name is the only thing
              identifying this panel, and "British Columbia" cut to "British
              Col..." is what the review flagged. */}
          <p className="line-clamp-2 font-['Inter'] text-[14px] font-bold leading-[19px] text-[#111827] dark:text-[#E6E8E6]">
            {title}
          </p>
        </div>
        {/* One control, two states: Expand scatters the group's locations
            across the map, Reset puts them back. They were separate buttons,
            which meant the second appearing on click shifted the first
            sideways and read as an icon morphing rather than a new control.
            As a toggle the slot never moves and only the glyph changes. */}
        {summary.total > 1 && (onExpandRegions || onCollapseRegions) && (
          <button
            type="button"
            onClick={() => (isExpanded ? onCollapseRegions?.(cluster) : onExpandRegions?.(cluster))}
            aria-pressed={isExpanded}
            title={isExpanded
              ? t('map.growing.resetGroupHint', 'Put these locations back behind one pin')
              : t('map.growing.expandHint', 'Show each location on the map without zooming in')}
            aria-label={isExpanded
              ? t('map.growing.resetGroup', 'Reset')
              : t('map.growing.expandRegions', 'Expand')}
            className={`flex h-[28px] w-[28px] shrink-0 cursor-pointer items-center justify-center rounded-full transition-[color,background-color] duration-150 ${
              isExpanded
                ? 'bg-[#F0FDF4] text-[#16A34A] hover:bg-[#DCF0E2] dark:bg-[#1C3023] dark:text-[#7EE6A4]'
                : 'bg-[#F3F5F2] text-[#5D625F] hover:bg-[#E8EDEA] dark:bg-[#252B27] dark:text-[#9AA09A]'
            }`}
          >
            {isExpanded ? <RotateCcw size={13} /> : <Ungroup size={14} />}
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
          className="flex h-[28px] w-[28px] shrink-0 cursor-pointer items-center justify-center rounded-full bg-[#F3F5F2] text-[#5D625F] transition-colors hover:bg-[#E8EDEA] dark:bg-[#252B27] dark:text-[#9AA09A]"
        >
          <X size={14} />
        </button>
      </div>

      {/* Totals. "Locations" counts the cities inside this region; it was a
          duplicate of the heading back when the heading was a count, and is
          not one now that the heading is the region's name.
          Area is omitted wherever the warehouse has none, rather than shown
          as a dash: planted area is being released region by region, so a
          missing figure means "not published yet", not "zero". The pin itself
          still draws — only this field goes. */}
      <div className={`flex shrink-0 divide-x border-b border-solid ${DIVIDER} divide-[#EEF0EC] dark:divide-[#2A302C]`}>
        <Total label={t('map.growing.locations', 'Locations')} value={formatFull(summary.total)} />
        {summary.areaHa != null && (
          <Total label={t('map.growing.area', 'Area')} value={formatCompact(summary.areaHa)} unit={t('map.growing.ha', 'ha')} />
        )}
        <Total label={t('map.growing.production', 'Production')} value={formatCompact(summary.productionT)} unit={t('map.growing.t', 't')} />
      </div>

      {/* Status groups */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {summary.groups.map((group) => {
          const isOpen = expanded === group.status;
          const seeAll = !!showAll[group.status];
          const rows = seeAll ? group.regions : group.regions.slice(0, COLLAPSED_ROWS);
          // Derived from every region in the group, not just the visible
          // rows, so expanding "show more" cannot make a column appear.
          const showArea = group.regions.some((r) => regionAreaHa(r) != null);
          const hidden = group.count - rows.length;
          return (
            <div key={group.status} className={`border-b border-solid ${DIVIDER} last:border-b-0`}>
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : group.status)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-[8px] py-[11px] pr-[12px] pl-[9px] text-left hover:bg-[#F7F9F7] dark:hover:bg-[#20251F]"
                style={{ borderLeft: `3px solid ${growingStatusColor(group.status)}` }}
              >
                <MapPin size={15} className="shrink-0" style={{ color: growingStatusColor(group.status) }} fill={growingStatusColor(group.status)} stroke="#fff" strokeWidth={1.6} />
                <span className="min-w-0 flex-1 truncate font-['Inter'] text-[13.5px] font-bold text-[#282A29] dark:text-[#E6E8E6]">
                  {t(`map.filters.productionStatus.${group.status}`, group.label)}
                </span>
                {group.areaHa != null && (
                  <span className="shrink-0 font-['Inter'] text-[12px] text-[#828382]">
                    {formatFull(group.areaHa)} {t('map.growing.ha', 'ha')}
                  </span>
                )}
                <span className="flex h-[20px] min-w-[26px] shrink-0 items-center justify-center rounded-[6px] border border-solid border-[#E6E9E4] bg-white px-[5px] font-['Inter'] text-[11px] font-bold text-[#111827] dark:border-[#2A302C] dark:bg-[#1d1d1d] dark:text-[#E6E8E6]">
                  {group.count}
                </span>
                {isOpen
                  ? <ChevronUp size={15} className="shrink-0 text-[#828382]" />
                  : <ChevronDown size={15} className="shrink-0 text-[#828382]" />}
              </button>

              {isOpen && (
                <div className="pb-[6px] pl-[12px] pr-[12px]">
                  <table className="w-full table-fixed border-collapse">
                    <thead>
                      {/* The unit belongs to the column, not to every cell in
                          it: "13,903 t" on each of six rows is the same word
                          six times. Stated once in the header, the rows carry
                          bare numbers and line up on their digits.

                          These figures repeat down the column because the
                          warehouse stores production per REGION and stamps it
                          onto every location row — see `regionProduction` in
                          growingClusterSummary, which is why the group total
                          above counts it once rather than once per row. */}
                      <tr className={LABEL}>
                        <th className="w-[44%] py-[4px] text-left font-normal">{t('map.growing.region', 'Region')}</th>
                        {showArea && (
                          <th className="py-[4px] text-right font-normal">{t('map.growing.areaHa', 'Area (ha)')}</th>
                        )}
                        <th className="py-[4px] text-right font-normal">{t('map.growing.productionT', 'Production (t)')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, i) => {
                        const rowKey = `${group.status}|${r.specific_location || r.region}|${r.latitude}|${r.longitude}|${i}`;
                        const isOpenRow = openRow === rowKey;
                        const name = toInitCap(r.specific_location || r.region) || '—';
                        const varieties = varietiesOf(r);
                        const windows = windowsOf(r);
                        // Region + Production, plus Area when the group has any.
                        const colCount = 2 + (showArea ? 1 : 0);
                        return (
                        <Fragment key={rowKey}>
                        {/* The design has no per-row controls, so the row
                            itself opens its detail — varieties and producing
                            window stay reachable without a chevron. */}
                        <tr
                          onClick={() => setOpenRow(isOpenRow ? null : rowKey)}
                          aria-expanded={isOpenRow}
                          className={`cursor-pointer border-t border-solid ${DIVIDER} hover:bg-[#F7F9F7] dark:hover:bg-[#20251F]`}
                        >
                          <td className="truncate py-[7px] pr-[6px] font-['Inter'] text-[11.5px] text-[#111827] dark:text-[#E6E8E6]">
                            {name}
                          </td>
                          {showArea && (
                            <td className="py-[7px] text-right font-['Inter'] text-[11.5px] text-[#4B5563] dark:text-[#E6E8E6]">
                              {formatFull(regionAreaHa(r))}
                            </td>
                          )}
                          <td className="py-[7px] text-right font-['Inter'] text-[11.5px] text-[#4B5563] dark:text-[#E6E8E6]">
                            {formatFull(regionProductionT(r))}
                          </td>
                        </tr>
                        {isOpenRow && (
                          <tr>
                            <td colSpan={colCount} className="pb-[7px]">
                              <div className="rounded-[8px] bg-[#F7F9F7] px-[9px] py-[6px] dark:bg-[#20251F]">
                                <DetailLine
                                  label={t('map.growing.varieties', 'Varieties')}
                                  value={varieties || t('map.growing.noData', 'Not recorded')}
                                />
                                <DetailLine
                                  label={t('map.growing.producingWindow', 'Producing window')}
                                  value={windows.length ? windows.join(' · ') : t('map.growing.noData', 'Not recorded')}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                        </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {hidden > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAll((s) => ({ ...s, [group.status]: true }))}
                      className="mt-[4px] cursor-pointer font-['Inter'] text-[11.5px] font-semibold text-[#15803D] hover:underline dark:text-[#7EE6A4]"
                    >
                      {t('map.growing.showMore', 'Show {{count}} more regions', { count: hidden })}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      {/* `flex-wrap` rather than truncation — cut labels are what this panel
          was pulled up on in review.

          An "Expand" button sat here, opening a group's pins in place without
          moving the camera. Removed for now at the reviewer's request; the
          mechanism behind it stays, because Zoom and the per-row magnifier
          both expand a group as part of what they do. */}
      <div className={`flex shrink-0 flex-wrap items-center gap-x-[12px] gap-y-[6px] border-t border-solid px-[12px] py-[9px] ${DIVIDER}`}>
        <button
          type="button"
          onClick={() => onZoomToRegions?.(cluster)}
          className="flex shrink-0 cursor-pointer items-center gap-[6px] font-['Inter'] text-[12.5px] font-semibold text-[#282A29] hover:text-[#16A34A] dark:text-[#E6E8E6] dark:hover:text-[#7EE6A4]"
        >
          <ZoomIn size={15} className="shrink-0" />
          {t('map.growing.zoomToRegions', 'Zoom to regions')}
        </button>
        {onAskBiq && (
          <button
            type="button"
            onClick={() => onAskBiq(cluster)}
            title={t('map.growing.askBiq', 'Ask BiQ about this cluster')}
            className="ml-auto flex h-[30px] shrink-0 cursor-pointer items-center gap-[5px] rounded-[8px] border border-solid border-[#16A34A]/35 bg-[#F0FDF4] px-[9px] font-['Inter'] text-[12px] font-bold whitespace-nowrap text-[#16A34A] transition-colors hover:bg-[#DCF0E2] dark:border-[#7EE6A4]/30 dark:bg-[#1C3023] dark:text-[#7EE6A4]"
          >
            <MessageSquare size={13} className="shrink-0" />
            {t('map.growing.askBiqShort', 'Ask BiQ')}
          </button>
        )}
      </div>
    </div>
  );
};

export default GrowingClusterOverview;
